import React, {
  createContext,
  useReducer,
  useContext,
  useEffect,
  useState,
} from 'react';

const SliderStateContext = createContext();
const SliderDispatchContext = createContext();

function next({ length, current }) {
  return (current + 1) % length;
}

function previous({ length, current }) {
  return (current - 1 + length) % length;
}

function reducer(state, { type }) {
  switch (type) {
    case 'NEXT':
      return {
        ...state,
        activeIndex: next({ length: state.length, current: state.activeIndex }),
      };
    case 'PREVIOUS':
      return {
        ...state,
        activeIndex: previous({
          length: state.length,
          current: state.activeIndex,
        }),
      };
    default:
      throw new Error(`Invalid action: ${type}`);
  }
}

function SliderProvider({ children, items = [] }) {
  const [state, dispatch] = useReducer(reducer, {
    length: items.length,
    activeIndex: 0,
  });
  const [activeItem, setActiveItem] = useState(items[0]);

  useEffect(() => {
    setActiveItem(items[state.activeIndex]);
  }, [items, state.activeIndex]);

  function nextItem() {
    dispatch({ type: 'NEXT' });
  }

  function previousItem() {
    dispatch({ type: 'PREVIOUS' });
  }

  const isFirstItemActive = items.indexOf(activeItem) === 0;

  const isLastItemActive = items.indexOf(activeItem) + 1 === state.length;

  return (
    <SliderStateContext.Provider
      value={{
        ...state,
        activeItem,
        isFirstItemActive,
        isLastItemActive,
        items,
      }}
    >
      <SliderDispatchContext.Provider
        value={{ dispatch, nextItem, previousItem }}
      >
        {children}
      </SliderDispatchContext.Provider>
    </SliderStateContext.Provider>
  );
}

function useSliderState() {
  const context = useContext(SliderStateContext);

  if (!context)
    throw new Error('useSliderState must be used within a SliderProvider');

  return context;
}

function useSliderDispatch() {
  const context = useContext(SliderDispatchContext);

  if (!context)
    throw new Error('useSliderDispatch must be used within a SliderProvider');

  return context;
}

export { SliderProvider, useSliderState, useSliderDispatch };
